import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, MaskLines } from "../components/site/Reveal";
import { INTRO_DELAY } from "../lib/api";

const VALUES = [
  ["01", "Freedom of movement", "Hands-free design that keeps up with your day, from the city to the trail."],
  ["02", "Quiet confidence", "Clean lines, considered colors and a single silver signature. Nothing extra."],
  ["03", "Made to last", "Durable materials and hardware chosen to look better the more you use it."],
];

export default function Story() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  return (
    <div data-testid="story-page" className="bg-ivory">
      <section ref={ref} className="relative h-[90svh] min-h-[560px] overflow-hidden bg-night text-white">
        <motion.img style={{ y }} src="/img/mountain.webp" alt="Mountain peaks at dawn" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
        <div className="relative mx-auto flex h-full max-w-[1600px] flex-col justify-end px-5 pb-16 md:px-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D8DCE0]">Our story</p>
          <MaskLines as="h1" delay={INTRO_DELAY} lines={["Born of", <em key="m">the mountain.</em>]} className="mt-4 font-display text-[14vw] font-light uppercase leading-[0.86] sm:text-[10vw] lg:text-[8vw]" />
        </div>
      </section>
      <section className="mx-auto grid max-w-[1600px] gap-12 px-5 py-24 md:px-10 md:py-36 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5"><img src="/img/emblem.png" alt="MARKHOR emblem" className="w-48 md:w-64" /></FadeUp>
        <FadeUp className="space-y-6 text-base leading-relaxed text-graphite lg:col-span-7">
          <p className="font-display text-3xl leading-snug text-ink md:text-4xl">The markhor is a wild mountain goat known for its spiraling horns and its ability to move with ease across the steepest terrain.</p>
          <p>We named our brand after it because it represents what we want every bag to give you: the freedom to move, the confidence to stand out, and a quiet strength that doesn't need to shout.</p>
          <p>Founded in Virginia, USA, MARKHOR was created with a vision to redefine modern fashion and lifestyle. Inspired by strength, elegance, and individuality, our mission is to bring timeless style, thoughtful design, and everyday confidence to people around the world.</p>
        </FadeUp>
      </section>
      <section className="border-t border-line">
        <div className="mx-auto grid max-w-[1600px] md:grid-cols-3">
          {VALUES.map(([n, t, d], i) => (
            <FadeUp key={n} delay={i * 0.08} className="border-b border-line px-5 py-14 md:border-b-0 md:border-r md:px-10">
              <p className="font-mono text-xs text-graphite">{n}</p>
              <p className="mt-6 font-display text-3xl text-ink">{t}</p>
              <p className="mt-3 max-w-xs text-sm text-graphite">{d}</p>
            </FadeUp>
          ))}
        </div>
      </section>
      <section className="py-24 text-center">
        <Link to="/shop" className="btn-dark" data-testid="story-shop-button">Shop the collection</Link>
      </section>
    </div>
  );
}
